/** GsNFj 更换邮箱页 — /profile/change-email（已绑定用户双码更换）
 *
 * 流程：当前邮箱获取验证码(send-code current) + 新邮箱获取验证码(send-code new)
 *       → 确认更换(change-email/confirm 双码) → 回 /profile/edit（提示用新邮箱登录）
 * spec: docs/superpowers/specs/2026-06-27-profile-email-verify-rebind-design.md §5.4
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { z } from 'zod'
import {
  ApiClientError,
  Spinner,
  useMyProfile,
  useChangeEmailSendCode,
  useChangeEmailConfirm,
} from '@dogdisk/shared'
import { AppLayout } from '../../components/app-layout'
import { MobileScreen, TopBar } from '../../components/mobile-screen'

const F = 'GsNFj'
const emailSchema = z.string().email()

export function ChangeEmailPage() {
  const navigate = useNavigate()
  const profileQuery = useMyProfile()
  const sendCode = useChangeEmailSendCode()
  const confirm = useChangeEmailConfirm()
  const [newEmail, setNewEmail] = useState('')
  const [curCode, setCurCode] = useState('')
  const [newCode, setNewCode] = useState('')
  const [curSent, setCurSent] = useState(false)
  const [newSent, setNewSent] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [ok, setOk] = useState(false)

  const profile = profileQuery.data
  const describe = (e: unknown) => {
    if (e instanceof ApiClientError) {
      if (e.code === 'EMAIL_DOMAIN_NOT_ALLOWED') return '仅支持 gmail.com、qq.com 等邮箱'
      return e.message || e.code
    }
    return e instanceof Error ? e.message : String(e)
  }

  if (!profile) {
    return (
      <AppLayout>
        <MobileScreen>
          <TopBar title="更换邮箱" fallback="/profile/edit" />
          <div className="flex flex-1 items-center justify-center py-12">
            <Spinner className="text-gray-400" />
          </div>
        </MobileScreen>
      </AppLayout>
    )
  }

  const sendCur = () => {
    setErr(null)
    sendCode.mutate(
      { target: 'current' },
      { onSuccess: () => setCurSent(true), onError: (e) => setErr(describe(e)) },
    )
  }
  const sendNew = () => {
    setErr(null)
    if (!emailSchema.safeParse(newEmail).success) {
      setErr('请输入合法的新邮箱')
      return
    }
    sendCode.mutate(
      { target: 'new', new_email: newEmail },
      { onSuccess: () => setNewSent(true), onError: (e) => setErr(describe(e)) },
    )
  }
  const onConfirm = () => {
    setErr(null)
    if (curCode.length !== 6 || newCode.length !== 6) {
      setErr('请填写两个 6 位验证码')
      return
    }
    confirm.mutate(
      { new_email: newEmail, current_code: curCode, new_code: newCode },
      {
        onSuccess: () => {
          setOk(true)
          setTimeout(() => navigate('/profile/edit'), 1500)
        },
        onError: (e) => setErr(describe(e)),
      },
    )
  }

  const codeInput = (
    testid: string,
    val: string,
    set: (v: string) => void,
    onSend: () => void,
    sent: boolean,
  ) => (
    <div className="flex gap-2">
      <input
        data-testid={testid}
        value={val}
        onChange={(e) => set(e.target.value.replace(/\D/g, ''))}
        maxLength={6}
        inputMode="numeric"
        placeholder="6 位验证码"
        className="h-11 flex-1 rounded-xl bg-[#F6F7F8] px-4 text-[14px] tracking-[6px] outline-none focus:ring-1 focus:ring-[#19caad]"
      />
      <button
        type="button"
        data-testid={`${testid}-send`}
        disabled={sendCode.isPending}
        onClick={onSend}
        className="shrink-0 rounded-xl bg-[#19caad] px-3 text-[13px] font-semibold text-white disabled:opacity-50"
      >
        {sent ? '重新发送' : '获取验证码'}
      </button>
    </div>
  )

  return (
    <AppLayout>
      <MobileScreen>
        <TopBar title="更换邮箱" fallback="/profile/edit" />
        <main data-testid={F} className="flex flex-1 flex-col gap-5 px-6 pt-4">
          <p className="text-[14px] text-[#6B7280]">为保障账号安全，更换邮箱需同时验证当前邮箱与新邮箱。</p>

          <section className="flex flex-col gap-2">
            <span className="text-[14px] font-semibold text-[#1A1A1A]">当前邮箱</span>
            <div className="flex h-12 items-center rounded-xl bg-[#F6F7F8] px-4 text-[14px] text-[#4B5563]">
              {profile.email_real ?? '—'}
            </div>
            {codeInput(`${F}-cur-code`, curCode, setCurCode, sendCur, curSent)}
          </section>

          <section className="flex flex-col gap-2">
            <span className="text-[14px] font-semibold text-[#1A1A1A]">新邮箱地址</span>
            <input
              data-testid={`${F}-new-email`}
              type="email"
              value={newEmail}
              onChange={(e) => {
                setNewEmail(e.target.value)
                setNewSent(false)
              }}
              placeholder="请输入新邮箱地址"
              className="h-12 rounded-xl bg-white px-4 text-[14px] outline-none ring-1 ring-[#E0E0E0] focus:ring-[#19caad]"
            />
            {codeInput(`${F}-new-code`, newCode, setNewCode, sendNew, newSent)}
          </section>

          {err && <p role="alert" className="text-[13px] text-red-600">{err}</p>}
          {ok && <p role="status" className="text-[13px] text-[#16A34A]">更换成功，请用新邮箱登录</p>}

          <button
            type="button"
            data-testid={`${F}-confirm`}
            disabled={confirm.isPending}
            onClick={onConfirm}
            className="mt-2 h-12 rounded-xl bg-[#19caad] text-[15px] font-semibold text-white disabled:opacity-50"
          >
            {confirm.isPending ? '提交中…' : '确认更换邮箱'}
          </button>
          <button
            type="button"
            data-testid={`${F}-cancel`}
            onClick={() => navigate('/profile/edit')}
            className="text-[14px] text-[#9AA3AE]"
          >
            取消
          </button>
        </main>
      </MobileScreen>
    </AppLayout>
  )
}

export default ChangeEmailPage
